import * as moment from 'moment';

/**
 * Schedule events on the calendar. The month endpoint sends one row per occurrence (a recurring
 * event is already expanded on the server), each running from startDate startTime to endDate
 * endTime. These helpers turn an occurrence into one entry per day it holds, the shape the
 * calendar views and the open-slots grid work with.
 */

export type Frequency = 'daily' | 'weekly' | 'monthly';
export type WeekdayCode = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';

export interface Recurrence {
  freq: Frequency;
  interval?: number;
  weekdays?: WeekdayCode[];
  until?: string;
  count?: number;
}

export const WEEKDAY_CODES: WeekdayCode[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
export const WEEKDAY_SHORT: Record<WeekdayCode, string> = { MO: 'Mon', TU: 'Tue', WE: 'Wed', TH: 'Thu', FR: 'Fri', SA: 'Sat', SU: 'Sun' };

const END_OF_DAY = '23:59';

/** A schedule event (or a calendar copy of one), not a patient booking. */
export function isScheduleEvent(row: any): boolean {
  if (!row || row.Patient || row.patientId) return false;
  return !!row.reason || row.appointmentType === 'Scheduled Event';
}

/** The event's code for its weekday, e.g. 'MO' for a Monday. */
export function weekdayCode(date: any): WeekdayCode {
  return WEEKDAY_CODES[moment(date).isoWeekday() - 1];
}

/**
 * One entry per day an occurrence holds, with that day's `date`, `timeSlot` and `endSlot`. A timed
 * event over several days holds from its start time on the first day to its end time on the last;
 * the days in between, and every day of an all-day event, are held whole (isAllDay).
 */
export function eventDayEntries(event: any): any[] {
  const first = moment(event.startDate).startOf('day');
  const last = moment(event.endDate || event.startDate).startOf('day');
  const timed = !event.isAllDay && !!(event.startTime || event.endTime);
  const entries: any[] = [];
  for (const day = first.clone(); day.isSameOrBefore(last, 'day'); day.add(1, 'day')) {
    const isFirst = day.isSame(first, 'day');
    const isLast = day.isSame(last, 'day');
    const timeSlot = timed && isFirst ? (event.startTime || '00:00') : '00:00';
    const endSlot = timed && isLast ? (event.endTime || END_OF_DAY) : END_OF_DAY;
    entries.push({
      ...event,
      date: day.toISOString(),
      timeSlot,
      endSlot,
      // Where this day sits in an event over several days; null for a one-day event.
      spanPart: isFirst && isLast ? null : isFirst ? 'first' : isLast ? 'last' : 'middle',
      isAllDay: !timed || (timeSlot === '00:00' && endSlot === END_OF_DAY),
      appointmentType: 'Scheduled Event',
      typeDetails: { type: event.reason, backgroundColor: '#424242', textColor: '#fff', borderColor: '#4caf50' },
    });
  }
  return entries;
}

/** Identifies one occurrence: a recurring event's rows share the event id. */
export function eventOccurrenceKey(event: any): string {
  const day = event.occurrenceDate || moment(event.startDate).format('YYYY-MM-DD');
  return `${event.id}|${day}`;
}

const ordinal = (n: number) => {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] || 'th'}`;
};

/** "Every week on Mon, Thu until 31 Dec 2026"; '' when the event does not repeat. */
export function describeRecurrence(rule: Recurrence | null | undefined, startDate?: any): string {
  if (!rule) return '';
  const interval = rule.interval && rule.interval > 1 ? rule.interval : 1;
  const unit = { daily: 'day', weekly: 'week', monthly: 'month' }[rule.freq];
  let text = interval > 1 ? `Every ${interval} ${unit}s` : `Every ${unit}`;
  if (rule.freq === 'weekly' && rule.weekdays?.length) {
    text += ` on ${rule.weekdays.map(code => WEEKDAY_SHORT[code]).join(', ')}`;
  }
  if (rule.freq === 'monthly' && startDate) {
    text += ` on the ${ordinal(moment(startDate).date())}`;
  }
  if (rule.until) text += ` until ${moment(rule.until, 'YYYY-MM-DD').format('D MMM YYYY')}`;
  else if (rule.count) text += `, ${rule.count} times`;
  return text;
}

/** Router extras for the event editor: a recurring event opens on the occurrence that was clicked. */
export function editEventExtras(event: any): { queryParams?: { occurrence: string } } {
  return event?.isRecurring && event.occurrenceDate ? { queryParams: { occurrence: event.occurrenceDate } } : {};
}

/** The editor's dates for one occurrence of a recurring event: same length, starting that day. */
export function occurrenceFormDates(event: any, occurrenceDate: string): { startDate: moment.Moment; endDate: moment.Moment } {
  const days = moment(event.endDate || event.startDate).startOf('day').diff(moment(event.startDate).startOf('day'), 'days');
  const startDate = moment(occurrenceDate, 'YYYY-MM-DD');
  return { startDate, endDate: startDate.clone().add(Math.max(days, 0), 'days') };
}

export type RepeatChoice = 'none' | Frequency;
export type RepeatEnd = 'never' | 'on' | 'after';

export interface RepeatForm {
  repeat: RepeatChoice;
  interval?: number;
  weekdays?: WeekdayCode[];
  ends?: RepeatEnd;
  until?: any;
  count?: number;
}

/** The rule the editor's repeat fields describe; weekly with no day ticked repeats on the start's weekday. */
export function recurrenceFromForm(form: RepeatForm, startDate: any): Recurrence | null {
  if (!form || form.repeat === 'none') return null;
  const rule: Recurrence = { freq: form.repeat, interval: Math.max(Math.floor(Number(form.interval) || 1), 1) };
  if (form.repeat === 'weekly') {
    const picked = WEEKDAY_CODES.filter(code => (form.weekdays || []).includes(code));
    rule.weekdays = picked.length ? picked : [weekdayCode(startDate)];
  }
  if (form.ends === 'on' && form.until) rule.until = moment(form.until).format('YYYY-MM-DD');
  if (form.ends === 'after' && Number(form.count) > 0) rule.count = Math.floor(Number(form.count));
  return rule;
}

/** The editor's repeat fields for a rule (or defaults for an event that does not repeat). */
export function recurrenceToForm(rule: Recurrence | null | undefined, startDate: any): Required<RepeatForm> {
  return {
    repeat: rule?.freq ?? 'none',
    interval: rule?.interval ?? 1,
    weekdays: rule?.weekdays?.length ? [...rule.weekdays] : [weekdayCode(startDate)],
    ends: rule?.until ? 'on' : rule?.count ? 'after' : 'never',
    until: rule?.until ? moment(rule.until, 'YYYY-MM-DD') : null,
    count: rule?.count ?? 10,
  };
}

/** "09:00–09:30" for a day's entry; a multi-day event reads "From 14:00", "All day", "Until 11:00". */
export function entryTimeLabel(entry: any): string {
  const start = String(entry?.timeSlot ?? '').slice(0, 5);
  const end = String(entry?.endSlot ?? '').slice(0, 5);
  if (entry?.isAllDay) return 'All day';
  if (entry?.spanPart === 'first') return `From ${start}`;
  if (entry?.spanPart === 'last') return `Until ${end}`;
  return end ? `${start}–${end}` : start;
}
